/**
 * Show More Listener (V2)
 * addEventListener / observer 登録のみを担当
 */

import type { ShowMoreForm } from "./show-more-form";
import type { ShowMoreHandler } from "./show-more-handler";

export function bindShowMoreTransitionEnd(panel: HTMLDivElement, onEnd: () => void): void {
    panel.addEventListener("transitionend", onEnd, { once: true });
}

export class ShowMoreListener {
    public form: ShowMoreForm;

    public handler: ShowMoreHandler;

    public constructor(form: ShowMoreForm, handler: ShowMoreHandler) {
        this.form = form;
        this.handler = handler;
    }

    public register(): void {
        const root = this.form.getRoot();
        if (root.dataset.showMoreListenerBound === "1") return;
        root.dataset.showMoreListenerBound = "1";

        const onToggleClick = (event: Event) => {
            const button = this.form.getClosestToggle(event.target);
            if (!(button instanceof HTMLButtonElement)) return;
            if (!root.contains(button)) return;

            this.handler.handleToggleClick(event);
        };

        root.addEventListener("click", onToggleClick, true);

        // Clamp mode: content itself is the expand trigger (collapsed state only)
        if (this.form.isClampMode()) {
            const content = this.form.contentElement;
            if (content instanceof HTMLElement) {
                content.addEventListener("click", () => {
                    if (!this.handler.expanded) {
                        this.handler.setExpanded(true, true, "user");
                    }
                });
            }
        }

        const observer = new MutationObserver((mutations) => {
            this.handler.handleRootMutations(mutations);
        });

        observer.observe(root, {
            attributes: true,
            attributeFilter: ["open", "label", "open-label", "label-key", "open-label-key", "duration", "lines"],
        });

        this.form.setObserver(observer);

        const content = this.form.contentElement;
        if (content instanceof HTMLElement && typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(() => {
                this.handler.handleLayoutChange();
            });
            resizeObserver.observe(content);
            resizeObserver.observe(root);
        }

        const onResize = () => this.handler.handleLayoutChange();
        if (typeof globalThis.addEventListener === "function") {
            globalThis.addEventListener("resize", onResize, { passive: true });
        }

        const onLanguageChanged = () => {
            this.handler.syncLabelOnly();
            this.handler.handleLayoutChange();
        };
        document.addEventListener("languageChanged", onLanguageChanged);
    }
}
